/**
 * 六彩视界 · 运行时主题服务
 * ---------------------------------------------------------------------------
 * 设计 token 定义在 assets/scss/theme.scss。本模块负责：
 *   1. 初始化主题（浅色 / 深色），必须在 Vue 挂载前执行，避免闪烁
 *   2. 运行时覆盖 token（主色可配置），通过给 <html> 写行内 style 实现
 *   3. localStorage 持久化，用户选择的主题下次登录仍生效
 *
 * 用法：
 *   import { initTheme, setThemeMode, setPrimaryColor, getTheme } from "@/utils/theme";
 *
 *   initTheme();                       // main.js 顶部调用一次
 *   setThemeMode("dark");              // 切换深色
 *   setPrimaryColor("#2E5AA8");        // 换主色
 *   resetTheme();                      // 恢复默认
 */

const STORAGE_KEY = "lcsj-theme";
export const DEFAULT_PRIMARY_COLOR = "#0e7c66"; // 松石绿
const DEFAULT_THEME = {
	mode: "light", // light | dark
	primaryColor: DEFAULT_PRIMARY_COLOR
};

// 需要在切换主色时同步覆盖的衍生色（基于主色推导）
const DERIVED_KEYS = [
	"app-primary",
	"app-primary-hover",
	"app-primary-active",
	"app-button-hover",
	"app-button-active",
	"app-primary-soft",
	"app-primary-soft-hover",
	"app-primary-border",
	"app-primary-text",
	"app-primary-contrast",
	"app-surface-selected",
	"app-sidebar-bg",
	"app-sidebar-brand",
	"app-topbar-bg",
	"el-color-primary",
	"el-color-primary-light-3",
	"el-color-primary-light-5",
	"el-color-primary-light-7",
	"el-color-primary-light-8",
	"el-color-primary-light-9",
	"el-color-primary-dark-2"
];

/* ---------------- 颜色工具 ---------------- */

function hexToRgb(hex) {
	let h = String(hex || "").trim().replace(/^#/, "");
	if (h.length === 3) {
		h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
	}
	if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
	return {
		r: parseInt(h.slice(0, 2), 16),
		g: parseInt(h.slice(2, 4), 16),
		b: parseInt(h.slice(4, 6), 16)
	};
}

function rgbToHex(r, g, b) {
	const to = (n) => {
		const v = Math.max(0, Math.min(255, Math.round(n)));
		return v.toString(16).padStart(2, "0");
	};
	return "#" + to(r) + to(g) + to(b);
}

function isValidColor(c) {
	return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(String(c || "").trim());
}

/** 将三位色值展开，保证原生取色器、预设选中态与存储一致。 */
function normalizeColor(color) {
	const rgb = isValidColor(color) && hexToRgb(color);
	return rgb ? rgbToHex(rgb.r, rgb.g, rgb.b) : "";
}

function mix(rgb, background, ratio) {
	const channel = (key) => rgb[key] * ratio + background[key] * (1 - ratio);
	return rgbToHex(channel("r"), channel("g"), channel("b"));
}

function luminance(rgb) {
	const channels = [rgb.r, rgb.g, rgb.b].map((value) => {
		const c = value / 255;
		return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
	});
	return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
}

function contrast(a, b) {
	const values = [luminance(a), luminance(b)];
	return (Math.max(...values) + 0.05) / (Math.min(...values) + 0.05);
}

/** 保留所选色相，调整文字亮度以适应浅色 / 深色背景。 */
function readableColor(rgb, background) {
	const target = luminance(background) > 0.179 ? { r: 0, g: 0, b: 0 } : { r: 255, g: 255, b: 255 };
	for (let step = 0; step <= 20; step++) {
		const color = mix(target, rgb, step / 20);
		if (contrast(hexToRgb(color), background) >= 4.5) return color;
	}
	return rgbToHex(target.r, target.g, target.b);
}

/** 在保证按钮文字可读的前提下，选择与原色差异更明显的交互色。
 * 单纯混入少量白色会被文字对比度修正抵消，部分主题几乎看不出 hover。
 */
function buttonInteractionColor(rgb, foreground, amount) {
	const candidates = [{ r: 0, g: 0, b: 0 }, { r: 255, g: 255, b: 255 }]
		.map(target => readableColor(hexToRgb(mix(target, rgb, amount)), foreground));
	return candidates.sort((a, b) => contrast(rgb, hexToRgb(b)) - contrast(rgb, hexToRgb(a)))[0];
}

/** 主色 → 深色（同色相压暗，用于导航背景） */
function darken(rgb, ratio) {
	const mix = (n) => Math.round(n * (1 - ratio));
	return rgbToHex(mix(rgb.r), mix(rgb.g), mix(rgb.b));
}

/* ---------------- 存储 ---------------- */

function readStore() {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return { ...DEFAULT_THEME };
		const parsed = JSON.parse(raw);
		return {
			mode: parsed.mode === "dark" ? "dark" : "light",
			primaryColor: normalizeColor(parsed.primaryColor) || DEFAULT_PRIMARY_COLOR
		};
	} catch (e) {
		return { ...DEFAULT_THEME };
	}
}

function writeStore(theme) {
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
	} catch (e) {
		// 隐私模式 / 存储已满时静默降级，不影响功能
	}
}

/* ---------------- 状态 ---------------- */

let currentTheme = { ...DEFAULT_THEME };

/**
 * 核心：把主题状态应用到 DOM。
 * 写法用 <html> 的行内 style，优先级高于 theme.scss 的 :root，
 * 因此无需额外的 class或样式表切换。
 */
function applyTheme() {
	const root = document.documentElement;
	if (!root) return;

	// 1) 明暗模式：走 data-theme 属性（对应 theme.scss 中的深色变量块）
	if (currentTheme.mode === "dark") {
		root.setAttribute("data-theme", "dark");
	} else {
		root.removeAttribute("data-theme");
	}

	// 2) 主色覆盖：先清掉上一次的自定义值，再写入本次
	DERIVED_KEYS.forEach((k) => root.style.removeProperty("--" + k));

	const custom = currentTheme.primaryColor;
	if (custom && isValidColor(custom)) {
		const rgb = hexToRgb(custom);
		if (rgb) {
			const dark = currentTheme.mode === "dark";
			const surface = hexToRgb(window.getComputedStyle(root).getPropertyValue("--app-surface").trim()) || hexToRgb(dark ? "#1c222c" : "#ffffff");
			const soft = mix(rgb, surface, dark ? 0.18 : 0.08);
			const foreground = luminance(rgb) > 0.179 ? "#000000" : "#ffffff";
			const hover = readableColor(hexToRgb(mix({ r: 255, g: 255, b: 255 }, rgb, 0.15)), hexToRgb(foreground));
			const active = readableColor(hexToRgb(darken(rgb, 0.18)), hexToRgb(foreground));
			const map = {
				"app-primary": custom,
				"app-primary-hover": hover,
				"app-primary-active": active,
				"app-button-hover": buttonInteractionColor(rgb, hexToRgb(foreground), 0.25),
				"app-button-active": buttonInteractionColor(rgb, hexToRgb(foreground), 0.38),
				"app-primary-soft": soft,
				"app-primary-soft-hover": mix(rgb, surface, dark ? 0.26 : 0.14),
				"app-primary-border": mix(rgb, surface, dark ? 0.45 : 0.28),
				"app-primary-text": readableColor(rgb, hexToRgb(soft)),
				"app-primary-contrast": foreground,
				"app-surface-selected": soft
			};
			Object.keys(map).forEach((k) => root.style.setProperty("--" + k, map[k]));

			// Element UI 变量同步
			root.style.setProperty("--el-color-primary", custom);
			root.style.setProperty("--el-color-primary-light-3", hover);
			root.style.setProperty("--el-color-primary-light-5", map["app-primary-border"]);
			root.style.setProperty("--el-color-primary-light-7", map["app-primary-soft-hover"]);
			root.style.setProperty("--el-color-primary-light-8", soft);
			root.style.setProperty("--el-color-primary-light-9", soft);
			root.style.setProperty("--el-color-primary-dark-2", active);

			// 侧栏/顶栏跟随主色（2026-10-09 许工要求：换主色后左侧菜单也应变色）。
			// 侧栏是深色底，所以取主色的「加深 + 压暗」版本，保证白字始终可读；
			// 不能直接用主色本身，否则浅色主色会让白字看不清。
			root.style.setProperty("--app-sidebar-bg", darken(rgb, 0.55));
			root.style.setProperty("--app-sidebar-brand", darken(rgb, 0.35));
			root.style.setProperty("--app-topbar-bg", darken(rgb, 0.62));
		}
	}

	// 3) 通知图表色缓存失效：echarts 读的是 CSS 变量的【实际值】并缓存，
	//    主题/主色一变必须清缓存，否则图表颜色停留在旧值。
	//    用自定义事件而非直接 import，避免 theme.js → chartColor.js 的循环依赖。
	try {
		window.dispatchEvent(new CustomEvent("lcsj-theme-changed"));
	} catch (e) {
		/* 老浏览器无 CustomEvent，忽略 */
	}
}

/* ---------------- 对外 API ---------------- */

/**
 * 初始化主题。必须在 new Vue() 之前同步调用（同步执行，避免刷新闪白/闪黑）。
 * @param {Object} [options] 可选：{ mode, primaryColor } 覆盖已存配置
 */
export function initTheme(options) {
	const stored = readStore();
	currentTheme = {
		mode: options && options.mode ? (options.mode === "dark" ? "dark" : "light") : stored.mode,
		primaryColor: options && options.primaryColor !== undefined ? (normalizeColor(options.primaryColor) || DEFAULT_PRIMARY_COLOR) : stored.primaryColor
	};
	applyTheme();
	return { ...currentTheme };
}

/** 切换明暗模式 */
export function setThemeMode(mode) {
	currentTheme.mode = mode === "dark" ? "dark" : "light";
	applyTheme();
	writeStore(currentTheme);
	return { ...currentTheme };
}

/** 读取当前模式 */
export function getThemeMode() {
	return currentTheme.mode;
}

/** 设置主色，传空字符串恢复默认 */
export function setPrimaryColor(color) {
	currentTheme.primaryColor = normalizeColor(color) || DEFAULT_PRIMARY_COLOR;
	applyTheme();
	writeStore(currentTheme);
	return { ...currentTheme };
}

/** 读取当前生效配置 */
export function getTheme() {
	return { ...currentTheme };
}

/** 恢复默认（浅色 + 松石绿） */
export function resetTheme() {
	currentTheme = { ...DEFAULT_THEME };
	applyTheme();
	writeStore(currentTheme);
	return { ...currentTheme };
}

/** 在明暗之间切换 */
export function toggleThemeMode() {
	return setThemeMode(currentTheme.mode === "dark" ? "light" : "dark");
}

export default {
	initTheme,
	setThemeMode,
	getThemeMode,
	setPrimaryColor,
	getTheme,
	resetTheme,
	toggleThemeMode
};
